import * as React from 'react';
import { Link } from 'react-router-dom';
import { Link as ExternalLink } from 'ui';
import {
  MdAccountCircle as MdUser,
  MdTrendingUp,
  MdMailOutline as MdMessage,
  MdPlaylistAddCheck,
  MdKeyboardArrowDown,
  MdKeyboardArrowUp,
  MdBuild
} from 'react-icons/md';
import { FaSitemap } from 'react-icons/fa';
import { routeConstants } from 'app/routes/route-constants';
import { shouldShowToolsMenu } from 'app/shell/should-show-tools-menu';
import { urlToSurveyManager } from 'app/area/favourite/components/url-to-survey-manager';
import { When } from './when';
import { canManageSurveys } from './menu-access-guards';
import { ToolsDashboardsReportsMenuItem } from './tools-dashboards-reports-menu-item';
import { ToolsImportSetsMenuItem } from './tools-import-sets-menu-item';
import { ToolsExperienceTransferMenuItem } from './tools-experience-transfer-menu-item';
import { ToolsExternalUrlsMenuItem } from './tools-external-urls-menu-item';
import { ToolsTranslationsMenuItem } from './tools-translations-menu-item';
import { ToolsOtherMenuItem } from './tools-other-menu-item';
import { MenuAccess } from '../../../types';
import styles from '../mobile-nav.scss';

interface ToolsMenuProps {
  translation: any;
  menuAccess: MenuAccess;
  manageSurveysUrl: string;
  onItemClick: () => void;
  onRefreshCache: (event: React.MouseEvent) => void;
  onRegenerateAggregatedEnv: (event: React.MouseEvent) => void;
  onRegenerateAggregatedRisk: (event: React.MouseEvent) => void;
}

export const ToolsMenu = ({
  translation,
  menuAccess,
  manageSurveysUrl,
  onItemClick,
  onRefreshCache,
  onRegenerateAggregatedEnv,
  onRegenerateAggregatedRisk
}: ToolsMenuProps) => {
  // menuAccess may be absent before access info has loaded
  const access = menuAccess || {} as MenuAccess;
  return (
  <When condition={shouldShowToolsMenu(access)}>
    <li>
      <input type="checkbox" id="mobile-admin" />

      <div className={`${styles.dropdownButton} list-group-item`}>
        <MdBuild className={styles.navItemIcon} />
        <span className={`${styles.navItemText} ${styles.small}`}>
          {translation.tools}
        </span>
        <label className={styles.dropdownLabel} htmlFor="mobile-admin">
          <MdKeyboardArrowDown className={styles.down} />
          <MdKeyboardArrowUp className={styles.up} />
        </label>
      </div>

      <ul className={styles.subMenu}>
        <When condition={access.codetableAdministration}>
          <li onClick={onItemClick}>
            <Link to={routeConstants.selectCodetableAdministration} className="list-group-item">
              <FaSitemap className={styles.navItemIcon} />
              <span className={styles.navItemText}>
                {translation.codetable_administration}
              </span>
            </Link>
          </li>
        </When>

        <When condition={access.userAdministration}>
          <li onClick={onItemClick}>
            <Link to={routeConstants.userSearchView} className="list-group-item">
              <MdUser className={styles.navItemIcon} />
              <span className={styles.navItemText}>
                {translation.user_administration}
              </span>
            </Link>
          </li>
        </When>

        <When condition={access.roleAdministration}>
          <li onClick={onItemClick}>
            <Link to={routeConstants.rolesView} className="list-group-item">
              <MdUser className={styles.navItemIcon} />
              <span className={styles.navItemText}>
                {translation.role_administration}
              </span>
            </Link>
          </li>
        </When>

        <ToolsDashboardsReportsMenuItem translation={translation} menuAccess={access} onItemClick={onItemClick} />
        <ToolsImportSetsMenuItem translation={translation} menuAccess={access} onItemClick={onItemClick} />
        <ToolsExperienceTransferMenuItem translation={translation} menuAccess={access} onItemClick={onItemClick} />

        <When condition={access.messageAdministration}>
          <li onClick={onItemClick}>
            <Link to={routeConstants.administrationMessagesList} className="list-group-item">
              <MdMessage className={styles.navItemIcon} />
              <span className={styles.navItemText}>
                {translation.messages_to_users}
              </span>
            </Link>
          </li>
        </When>

        <ToolsExternalUrlsMenuItem translation={translation} menuAccess={access} onItemClick={onItemClick} />

        <When condition={canManageSurveys(access, manageSurveysUrl)}>
          <li>
            <ExternalLink url={urlToSurveyManager(manageSurveysUrl)} className={'list-group-item'}>
              <MdPlaylistAddCheck className={styles.navItemIcon} />
              <span className={`${styles.navItemText} ${styles.small}`}>
                {translation.manage_surveys}
              </span>
            </ExternalLink>
          </li>
        </When>

        <When condition={access.imports}>
          <li onClick={onItemClick}>
            <Link to={routeConstants.importView} className="list-group-item">
              <MdTrendingUp className={styles.navItemIcon} />
              <span className={styles.navItemText}>{translation.import_title}</span>
            </Link>
          </li>
        </When>

        <ToolsTranslationsMenuItem translation={translation} menuAccess={access} onItemClick={onItemClick} />
        <ToolsOtherMenuItem
          translation={translation}
          menuAccess={access}
          onRefreshCache={onRefreshCache}
          onRegenerateAggregatedEnv={onRegenerateAggregatedEnv}
          onRegenerateAggregatedRisk={onRegenerateAggregatedRisk}
        />
      </ul>
    </li>
  </When>
  );
};
